import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OutputParameterInterface, RuleInterface } from '@vendasta/automata';
import { GalaxyInputModule, GalaxyInputValidator } from '@vendasta/galaxy/input';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { urlValidatorWithBusinessIdAndSalesUrlReplacement } from '../../../common/utils/url-validator';
import { GenericNotificationID, ShoppingCartAbandonedNotificationID } from '../constants';
import { CommonModule } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { CapitalizePipe } from '../../../../../common/components/capitalize.pipe';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { MatCheckboxModule } from '@angular/material/checkbox';

type notificationType = 'email' | 'web';

@Component({
  selector: 'automata-send-notification-to-the-user-panel',
  templateUrl: './send-notification-to-the-user-panel.component.html',
  styleUrls: ['./send-notification-to-the-user-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    CapitalizePipe,
    GalaxyWrapModule,
    MatCheckboxModule,
    GalaxyInputModule,
  ],
})
/* @deprecated: use SendNotificationUserPanelComponent instead */
export class SendNotificationToTheUserPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  notificationIdControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  notificationTypesArray: UntypedFormArray;
  notificationTypesGroup: UntypedFormGroup;
  subjectControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  linkTemplateControl: UntypedFormControl = new UntypedFormControl(
    '',
    urlValidatorWithBusinessIdAndSalesUrlReplacement,
  );
  buttonTextControl: UntypedFormControl = new UntypedFormControl('');
  bodyTextControl: UntypedFormControl = new UntypedFormControl('');
  form: UntypedFormGroup;

  destroyed$$ = new Subject<void>();

  linkValidators: GalaxyInputValidator[] = [
    {
      validatorFn: urlValidatorWithBusinessIdAndSalesUrlReplacement,
      errorMessage: this.translateService.instant('AUTOMATIONS.VALIDATORS.INVALID_URL'),
    },
  ];

  genericNotificationID = GenericNotificationID;
  shoppingCartAbandonedNotificationID = ShoppingCartAbandonedNotificationID;

  notificationTypeOptions: { name: string; value: notificationType }[] = [
    { name: 'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.NOTIFICATION_TYPES.IN_APP', value: 'web' },
    { name: 'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATION_TO_THE_USER.NOTIFICATION_TYPES.EMAIL', value: 'email' },
  ];

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly fb: UntypedFormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.notificationIdControl.setValue(jsonData.notification_id);
    const notificationTypes = JSON.parse(jsonData?.notification_types || '[]');
    this.notificationTypesArray = this.fb.array([
      notificationTypes.includes('web'),
      notificationTypes.includes('email'),
    ]);
    this.notificationTypesGroup = this.fb.group({
      notificationTypesArray: this.notificationTypesArray,
    });
    this.subjectControl.setValue(jsonData?.notification_subject_template || '');
    this.linkTemplateControl.setValue(jsonData?.notification_link_template || '');
    this.buttonTextControl.setValue(jsonData?.notification_button_text_template || '');
    this.bodyTextControl.setValue(jsonData?.notification_text_template || '');
    this.form = this.fb.group({
      subject: this.subjectControl,
      body: this.bodyTextControl,
      buttonText: this.buttonTextControl,
      link: this.linkTemplateControl,
    });

    combineLatest([
      this.notificationIdControl.valueChanges,
      this.notificationTypesArray.valueChanges.pipe(startWith(null)),
    ])
      .pipe(
        takeUntil(this.destroyed$$),
        map(([nId, nTypes]) => {
          this.updateValidators(nId, nTypes);
        }),
      )
      .subscribe();

    // There's nothing asynchronous here, so we can simply enable saving.
    this.savingEnabled = true;
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  get notificationTypes(): UntypedFormArray {
    return this.notificationTypesArray;
  }

  updateValidators(notificationId: string, notificationTypes: boolean[]): void {
    if (notificationId === this.genericNotificationID) {
      this.updateFormFieldValidators(
        this.notificationTypesArray,
        Validators.required,
        validateAtLeastOneCheckboxChecked(),
      );
      this.updateFormFieldValidators(this.bodyTextControl, Validators.required);
      this.updateFormFieldValidators(
        this.linkTemplateControl,
        Validators.required,
        urlValidatorWithBusinessIdAndSalesUrlReplacement,
      );
    } else {
      this.updateFormFieldValidators(this.notificationTypesArray);
      this.updateFormFieldValidators(this.bodyTextControl);
      this.updateFormFieldValidators(this.linkTemplateControl);
    }
    if (notificationTypes && notificationTypes.length >= 2 && notificationTypes[1] === true) {
      this.updateFormFieldValidators(this.subjectControl, Validators.required);
      this.updateFormFieldValidators(this.buttonTextControl, Validators.required);
    } else {
      this.updateFormFieldValidators(this.subjectControl);
      this.updateFormFieldValidators(this.buttonTextControl);
    }
    this.form.updateValueAndValidity();
  }

  updateFormFieldValidators(f: UntypedFormControl | UntypedFormArray, ...validators: ValidatorFn[]): void {
    if (!validators) {
      f.clearValidators();
    } else {
      f.setValidators(validators);
    }
    f.updateValueAndValidity({ emitEvent: false });
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }
    const notificationTypes = this.notificationTypeOptions
      .filter((x, i) => !!this.notificationTypesArray.at(i).value)
      .map((v) => v.value);
    const notificationTypesJSON = JSON.stringify(notificationTypes);

    jsonData.notification_id = this.notificationIdControl.value;
    jsonData.notification_types = notificationTypesJSON;
    jsonData.notification_subject_template = this.subjectControl.value;
    jsonData.notification_text_template = this.bodyTextControl.value;
    jsonData.notification_button_text_template = this.buttonTextControl.value;
    jsonData.notification_link_template = this.linkTemplateControl.value;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return !this.form.invalid;
  }

  save(name: string, customOutputParameters: OutputParameterInterface[]): Observable<boolean> {
    this.notificationTypesArray.markAsDirty();
    return super.save(name, customOutputParameters);
  }
}

export function validateAtLeastOneCheckboxChecked(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const arr = control as UntypedFormArray;
    const val = arr.getRawValue() as boolean[];
    if (!val || val.length < 2 || !val.some((v) => v === true)) {
      return { atLeastOne: true };
    }
    return null;
  };
}
